import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
} from 'react'
import {
  Circle, GoogleMap, Polyline, Marker, useJsApiLoader, InfoWindow,
} from '@react-google-maps/api'
import type { Libraries } from '@googlemaps/js-api-loader'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSatellite } from '@fortawesome/free-solid-svg-icons'
import { getFromNowAsText } from '../../helpers/timeHelper'
import { firstMapPinIcon } from '../../icons'
import { GOOGLE_MAP_API_KEY } from '../../constants/Constants'
import incrementGoogleMapsLoadCount from '../../utils/googleMapsLoadCounter'
import './DetailMamorioMap.css'

type Props = {
  date: string
  jt808Logs: IJT808Log[]
  wifiLocations?: IGpsTrackerWifiLocation[]
  cellularLocations?: IGpsTrackerCellularLocation[]
  updateGpsTrackerLogs: (arg: string) => Promise<void>
  downloadLogs: () => void
  currentStatus?: IGpsTrackerStatus
  detailHistoryEnabled?: boolean
}

type GpsTrackerCoordinate = {
  source: 'gps'
  lat: number
  lng: number
  timestamp: string
  speed?: string | number | null
}

type CellularTrackerCoordinate = {
  source: 'cellular'
  lat: number
  lng: number
  timestamp: string
  accuracyM: number | null
}

type WifiTrackerCoordinate = {
  source: 'wifi'
  lat: number
  lng: number
  timestamp: string
  accuracyM: number | null
}

type GpsWifiTrackerCoordinate = GpsTrackerCoordinate | WifiTrackerCoordinate
type TrackerCoordinate = GpsWifiTrackerCoordinate | CellularTrackerCoordinate
type LocationSource = 'gpsWifi' | 'cellular'

type GpsWifiCoordinateCombination = {
  coordinates: GpsWifiTrackerCoordinate[]
  gpsRetained: boolean
  wifiRetained: boolean
}

const parseCoordinateValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const numberValue = typeof value === 'number' ? value : parseFloat(String(value))
  return Number.isFinite(numberValue) ? numberValue : null
}

const parseFiniteNumber = (value: unknown) => {
  if (value === null || value === undefined) {
    return null
  }

  const normalizedValue = typeof value === 'string' ? value.trim() : value
  if (normalizedValue === '') {
    return null
  }

  const numberValue = typeof normalizedValue === 'number'
    ? normalizedValue
    : Number(normalizedValue)
  return Number.isFinite(numberValue) ? numberValue : null
}

const hasGpsFix = (log: IJT808Log) => log.has_fix === true || log.hasFix === true
const LOCATION_SOURCE_FRESHNESS_THRESHOLD_MS = 60 * 60 * 1000
const CELLULAR_ESTIMATE_RADIUS_M = 750
const CELLULAR_ESTIMATE_MARKER_ICON = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48"%3E%3Ccircle cx="24" cy="24" r="17" fill="%231F95F4" fill-opacity="0.2" stroke="%231F95F4" stroke-width="2"/%3E%3Ccircle cx="24" cy="24" r="6" fill="%231F95F4"/%3E%3Cpath d="M24 3v10M24 35v10M3 24h10M35 24h10" stroke="%231F95F4" stroke-width="2"/%3E%3C/svg%3E'
const EMPTY_WIFI_LOCATIONS: IGpsTrackerWifiLocation[] = []
const EMPTY_CELLULAR_LOCATIONS: IGpsTrackerCellularLocation[] = []

const getTimestampTime = (timestamp?: string | null) => {
  if (!timestamp) return Number.NEGATIVE_INFINITY

  const time = new Date(timestamp).getTime()
  return Number.isFinite(time) ? time : Number.NEGATIVE_INFINITY
}

function normalizeCoordinateOrder<Coordinate extends TrackerCoordinate>(
  locationCoordinates: Coordinate[],
): Coordinate[] {
  return [...locationCoordinates].sort(
    (a, b) => getTimestampTime(a.timestamp) - getTimestampTime(b.timestamp),
  )
}

const getNewerCoordinate = <Coordinate extends TrackerCoordinate>(
  firstCoordinate: Coordinate | null | undefined,
  secondCoordinate: Coordinate | null | undefined,
): Coordinate | null => {
  if (!firstCoordinate) return secondCoordinate || null
  if (!secondCoordinate) return firstCoordinate

  return getTimestampTime(secondCoordinate.timestamp) > getTimestampTime(firstCoordinate.timestamp)
    ? secondCoordinate
    : firstCoordinate
}

const combineGpsAndWifiCoordinates = (
  gpsCoordinates: GpsTrackerCoordinate[],
  wifiCoordinates: WifiTrackerCoordinate[],
  gpsFreshnessCoordinate: GpsTrackerCoordinate | null,
  wifiFreshnessCoordinate: WifiTrackerCoordinate | null,
): GpsWifiCoordinateCombination => {
  if (gpsCoordinates.length === 0) {
    return {
      coordinates: wifiCoordinates,
      gpsRetained: false,
      wifiRetained: wifiCoordinates.length > 0,
    }
  }
  if (wifiCoordinates.length === 0) {
    return {
      coordinates: gpsCoordinates,
      gpsRetained: gpsCoordinates.length > 0,
      wifiRetained: false,
    }
  }

  const latestGpsCoordinate = gpsFreshnessCoordinate
    || gpsCoordinates[gpsCoordinates.length - 1]!
  const latestWifiCoordinate = wifiFreshnessCoordinate
    || wifiCoordinates[wifiCoordinates.length - 1]!
  const latestGpsTime = getTimestampTime(latestGpsCoordinate.timestamp)
  const latestWifiTime = getTimestampTime(latestWifiCoordinate.timestamp)

  if (latestWifiTime - latestGpsTime >= LOCATION_SOURCE_FRESHNESS_THRESHOLD_MS) {
    return {
      coordinates: wifiCoordinates,
      gpsRetained: false,
      wifiRetained: true,
    }
  }

  if (latestGpsTime - latestWifiTime >= LOCATION_SOURCE_FRESHNESS_THRESHOLD_MS) {
    return {
      coordinates: gpsCoordinates,
      gpsRetained: true,
      wifiRetained: false,
    }
  }

  const coordinatesByTimestamp = new Map<number, GpsWifiTrackerCoordinate>()
  wifiCoordinates.forEach((coordinate) => {
    coordinatesByTimestamp.set(getTimestampTime(coordinate.timestamp), coordinate)
  })
  gpsCoordinates.forEach((coordinate) => {
    coordinatesByTimestamp.set(getTimestampTime(coordinate.timestamp), coordinate)
  })

  return {
    coordinates: normalizeCoordinateOrder([...coordinatesByTimestamp.values()]),
    gpsRetained: true,
    wifiRetained: true,
  }
}

const isValidLocationCoordinate = (lat: number | null, lng: number | null) => (
  lat !== null
  && lng !== null
  && lat >= -90
  && lat <= 90
  && lng >= -180
  && lng <= 180
)

const normalizeAccuracy = (value: unknown, fallback: number | null) => {
  const accuracyM = parseFiniteNumber(value)
  return accuracyM !== null && accuracyM > 0 ? accuracyM : fallback
}

const getPreferredLocationSource = (
  gpsWifiCoordinate: GpsWifiTrackerCoordinate | null,
  cellularCoordinate: CellularTrackerCoordinate | null,
): LocationSource | null => {
  if (!gpsWifiCoordinate) return cellularCoordinate ? 'cellular' : null
  if (!cellularCoordinate) return 'gpsWifi'

  const gpsAgeComparedWithCellular = getTimestampTime(cellularCoordinate.timestamp)
    - getTimestampTime(gpsWifiCoordinate.timestamp)

  return gpsAgeComparedWithCellular >= LOCATION_SOURCE_FRESHNESS_THRESHOLD_MS
    ? 'cellular'
    : 'gpsWifi'
}

const DetailGpsTrackerMap = ({
  date,
  jt808Logs,
  wifiLocations = EMPTY_WIFI_LOCATIONS,
  cellularLocations = EMPTY_CELLULAR_LOCATIONS,
  updateGpsTrackerLogs,
  downloadLogs,
  currentStatus,
  detailHistoryEnabled = false,
}: Props) => {
  const [selectedLogIndex, setSelectedLogIndex] = useState(0)
  const [libraries] = useState<Libraries>(['visualization'])
  const [size, setSize] = useState<undefined | google.maps.Size>(undefined)
  const [showInfoWindow, setShowInfoWindow] = useState<boolean>(false)
  const [infoWindowPosition, setInfoWindowPosition] = useState<{ lat: number; lng: number } | null>(null)

  const {
    isLoaded,
    loadError,
  } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAP_API_KEY,
    libraries,
  })
  const hasCountedLoadRef = useRef(false)
  const userSelectedSourceRef = useRef<LocationSource | null>(null)

  const infoWindowOptions = {
    pixelOffset: size,
  }
  const updateOffsetSize = () => setSize(new window.google.maps.Size(0, 0))

  const statusCoordinate = useMemo<GpsTrackerCoordinate | null>(() => {
    if (currentStatus && currentStatus.gps_location_updated_at) {
      const lat = parseCoordinateValue(currentStatus.latitude)
      const lng = parseCoordinateValue(currentStatus.longitude)

      if (lat === null || lng === null) {
        return null
      }

      return {
        source: 'gps',
        lat,
        lng,
        timestamp: currentStatus.gps_location_updated_at,
        speed: currentStatus.speed,
      }
    }
    return null
  }, [currentStatus])

  const fixedLogCoordinates = useMemo<GpsTrackerCoordinate[]>(() => {
    const fixedCoords = jt808Logs
      .filter(hasGpsFix)
      .map((log): GpsTrackerCoordinate | null => {
        const lat = parseCoordinateValue(log.latitude)
        const lng = parseCoordinateValue(log.longitude)

        if (lat === null || lng === null) {
          return null
        }

        return {
          source: 'gps',
          lat,
          lng,
          timestamp: log.timestamp,
          speed: log.speed,
        }
      })
      .filter((coord): coord is GpsTrackerCoordinate => coord !== null)
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())

    return fixedCoords
  }, [jt808Logs])

  const gpsCoordinates = useMemo<GpsTrackerCoordinate[]>(() => {
    if (fixedLogCoordinates.length > 0) {
      return fixedLogCoordinates
    }

    if (statusCoordinate) {
      return [statusCoordinate]
    }

    return []
  }, [fixedLogCoordinates, statusCoordinate])

  const statusCellularCoordinate = useMemo<CellularTrackerCoordinate | null>(() => {
    const cellular = currentStatus?.cellular

    if (!cellular || cellular.resolution_status !== 'resolved') {
      return null
    }

    const lat = parseFiniteNumber(cellular.latitude)
    const lng = parseFiniteNumber(cellular.longitude)

    if (!isValidLocationCoordinate(lat, lng)) {
      return null
    }

    return {
      source: 'cellular',
      lat: lat as number,
      lng: lng as number,
      timestamp: cellular?.observed_at || '',
      accuracyM: CELLULAR_ESTIMATE_RADIUS_M,
    }
  }, [currentStatus])

  const statusWifiCoordinate = useMemo<WifiTrackerCoordinate | null>(() => {
    const wifi = currentStatus?.wifi

    if (!wifi || wifi.resolution_status !== 'resolved') {
      return null
    }

    const lat = parseFiniteNumber(wifi.latitude)
    const lng = parseFiniteNumber(wifi.longitude)
    if (!isValidLocationCoordinate(lat, lng)) {
      return null
    }

    return {
      source: 'wifi',
      lat: lat as number,
      lng: lng as number,
      timestamp: wifi.observed_at || '',
      accuracyM: normalizeAccuracy(wifi.accuracy_m, null),
    }
  }, [currentStatus])

  const normalizedWifiCoordinates = useMemo<WifiTrackerCoordinate[]>(() => (
    normalizeCoordinateOrder(wifiLocations
      .map((location): WifiTrackerCoordinate | null => {
        const lat = parseFiniteNumber(location.latitude)
        const lng = parseFiniteNumber(location.longitude)

        if (
          !isValidLocationCoordinate(lat, lng)
          || getTimestampTime(location.timestamp) === Number.NEGATIVE_INFINITY
        ) {
          return null
        }

        return {
          source: 'wifi',
          lat: lat as number,
          lng: lng as number,
          timestamp: location.timestamp,
          accuracyM: normalizeAccuracy(location.accuracy_m, null),
        }
      })
      .filter((coordinate): coordinate is WifiTrackerCoordinate => coordinate !== null))
  ), [wifiLocations])

  const normalizedCellularCoordinates = useMemo<CellularTrackerCoordinate[]>(() => (
    normalizeCoordinateOrder(cellularLocations
      .map((location): CellularTrackerCoordinate | null => {
        const lat = parseFiniteNumber(location.latitude)
        const lng = parseFiniteNumber(location.longitude)

        if (
          !isValidLocationCoordinate(lat, lng)
          || getTimestampTime(location.timestamp) === Number.NEGATIVE_INFINITY
        ) {
          return null
        }

        return {
          source: 'cellular',
          lat: lat as number,
          lng: lng as number,
          timestamp: location.timestamp,
          accuracyM: CELLULAR_ESTIMATE_RADIUS_M,
        }
      })
      .filter((coordinate): coordinate is CellularTrackerCoordinate => coordinate !== null))
  ), [cellularLocations])

  const wifiCoordinates = useMemo<WifiTrackerCoordinate[]>(() => {
    if (normalizedWifiCoordinates.length > 0) {
      return normalizedWifiCoordinates
    }

    return statusWifiCoordinate ? [statusWifiCoordinate] : []
  }, [normalizedWifiCoordinates, statusWifiCoordinate])

  const cellularCoordinates = useMemo<CellularTrackerCoordinate[]>(() => {
    if (normalizedCellularCoordinates.length > 0) {
      return normalizedCellularCoordinates
    }

    return statusCellularCoordinate ? [statusCellularCoordinate] : []
  }, [normalizedCellularCoordinates, statusCellularCoordinate])

  const latestGpsCoordinate = gpsCoordinates[gpsCoordinates.length - 1] || null
  const latestWifiCoordinate = wifiCoordinates[wifiCoordinates.length - 1] || null
  const gpsFreshnessCoordinate = getNewerCoordinate(latestGpsCoordinate, statusCoordinate)
  const wifiFreshnessCoordinate = getNewerCoordinate(latestWifiCoordinate, statusWifiCoordinate)
  const gpsWifiCombination = useMemo<GpsWifiCoordinateCombination>(() => (
    combineGpsAndWifiCoordinates(
      gpsCoordinates,
      wifiCoordinates,
      gpsFreshnessCoordinate,
      wifiFreshnessCoordinate,
    )
  ), [
    gpsCoordinates,
    gpsFreshnessCoordinate,
    wifiCoordinates,
    wifiFreshnessCoordinate,
  ])
  const {
    coordinates: gpsWifiCoordinates,
    gpsRetained,
    wifiRetained,
  } = gpsWifiCombination

  const latestGpsWifiCoordinate = getNewerCoordinate<GpsWifiTrackerCoordinate>(
    gpsRetained ? gpsFreshnessCoordinate : null,
    wifiRetained ? wifiFreshnessCoordinate : null,
  )
  const latestCellularCoordinate = cellularCoordinates[cellularCoordinates.length - 1] || null
  const [locationSource, setLocationSource] = useState<LocationSource | null>(() => (
    getPreferredLocationSource(
      latestGpsWifiCoordinate,
      latestCellularCoordinate,
    )
  ))
  const hasGpsWifiLocation = gpsWifiCoordinates.length > 0
  const hasCellularLocation = cellularCoordinates.length > 0

  useEffect(() => {
    const userSelectedSource = userSelectedSourceRef.current
    const sourceAvailability: Record<LocationSource, boolean> = {
      gpsWifi: hasGpsWifiLocation,
      cellular: hasCellularLocation,
    }
    const userSelectedSourceIsAvailable = userSelectedSource
      ? sourceAvailability[userSelectedSource]
      : false

    setLocationSource(userSelectedSourceIsAvailable
      ? userSelectedSource
      : getPreferredLocationSource(
        latestGpsWifiCoordinate,
        latestCellularCoordinate,
      ))
  }, [
    hasCellularLocation,
    hasGpsWifiLocation,
    latestCellularCoordinate,
    latestGpsWifiCoordinate,
  ])

  const coordinates = useMemo<TrackerCoordinate[]>(() => {
    if (locationSource === 'cellular') {
      return cellularCoordinates
    }

    return locationSource === 'gpsWifi' ? gpsWifiCoordinates : []
  }, [cellularCoordinates, gpsWifiCoordinates, locationSource])

  const handleLocationSourceChange = (source: LocationSource) => {
    userSelectedSourceRef.current = source
    setLocationSource(source)
  }

  const renderLocationSourceControl = () => (
    <div
      className="gps-tracker-location-source-control"
      role="group"
      aria-label="表示する位置情報"
    >
      <button
        type="button"
        className={`gps-tracker-location-source-button${locationSource === 'gpsWifi' ? ' is-selected' : ''}`}
        aria-pressed={locationSource === 'gpsWifi'}
        disabled={!hasGpsWifiLocation}
        onClick={() => handleLocationSourceChange('gpsWifi')}
      >
        GPS+Wi-Fi
      </button>
      <button
        type="button"
        className={`gps-tracker-location-source-button${locationSource === 'cellular' ? ' is-selected' : ''}`}
        aria-pressed={locationSource === 'cellular'}
        disabled={!hasCellularLocation}
        onClick={() => handleLocationSourceChange('cellular')}
      >
        携帯基地局
      </button>
    </div>
  )

  const renderDateControl = () => {
    if (!detailHistoryEnabled) {
      return null
    }

    return (
      <div className="date-conatiner">
        <label>日付</label>
        <input
          className="date"
          value={date}
          type="date"
          onChange={(e) => {
            updateGpsTrackerLogs(e.target.value).catch(() => {
              // updateGpsTrackerLogs内でエラーハンドリング済み
            })
          }}
        />
        {jt808Logs.length + wifiLocations.length > 0 ? (
          <button type="button" className="btn btn-primary btn-sm" onClick={downloadLogs}>
            CSVをダウンロード(
            {jt808Logs.length + wifiLocations.length}
            件)
          </button>
        ) : (
          <button type="button" disabled className="btn btn-primary btn-sm">
            GPS+Wi-Fiログがありません
          </button>
        )}
      </div>
    )
  }

  const renderNoLocation = () => (
    <div className="detail-modal-content-map">
      {renderLocationSourceControl()}
      <div className="map-view">
        <div className="gps-tracker-location-empty">
          位置情報が取得できていません
        </div>
      </div>
      {renderDateControl()}
    </div>
  )

  // slider関連の計算
  const { minTimestamp, maxTimestamp } = useMemo(() => {
    if (coordinates.length === 0) return { minTimestamp: 0, maxTimestamp: 0 }
    const timestamps = coordinates.map((coord) => getTimestampTime(coord.timestamp))
    const min = Math.min(...timestamps)
    const max = Math.max(...timestamps)
    return { minTimestamp: min, maxTimestamp: max }
  }, [coordinates])

  const activeIndex = useMemo(() => {
    if (coordinates.length === 0) return null
    return Math.min(selectedLogIndex, coordinates.length - 1)
  }, [coordinates, selectedLogIndex])

  const polylinePath = useMemo(() => {
    if (locationSource !== 'gpsWifi' || activeIndex === null) {
      return []
    }

    const pathCoordinates = coordinates.slice(0, activeIndex + 1)

    return pathCoordinates.map((coord) => ({ lat: coord.lat, lng: coord.lng }))
  }, [activeIndex, coordinates, locationSource])

  const activeCoordinate = useMemo(() => {
    if (activeIndex === null) return null
    return coordinates[activeIndex]
  }, [activeIndex, coordinates])

  const mapCenter = useMemo(() => {
    if (activeCoordinate) {
      return { lat: activeCoordinate.lat, lng: activeCoordinate.lng }
    }
    return { lat: 35.6762, lng: 139.6503 }
  }, [activeCoordinate])

  const activeAccuracyM = activeCoordinate && 'accuracyM' in activeCoordinate
    ? activeCoordinate.accuracyM
    : null

  const sliderValue = useMemo(() => {
    if (!activeCoordinate) return 0
    const time = getTimestampTime(activeCoordinate.timestamp)
    return time === Number.NEGATIVE_INFINITY ? 0 : time
  }, [activeCoordinate])

  const sliderPercentage = useMemo(() => {
    if (minTimestamp === maxTimestamp) return 0
    return Math.min(((sliderValue - minTimestamp) / (maxTimestamp - minTimestamp)) * 100, 88)
  }, [sliderValue, minTimestamp, maxTimestamp])

  const sliderLabel = useMemo(() => {
    if (activeCoordinate && activeCoordinate.timestamp) {
      return getFromNowAsText(activeCoordinate.timestamp)
    }
    return ''
  }, [activeCoordinate])

  const handleMarkerClick = (index: number) => {
    setSelectedLogIndex(index)
    const coord = coordinates[index]
    if (coord) {
      setInfoWindowPosition({ lat: coord.lat, lng: coord.lng })
      setShowInfoWindow(true)
    }
  }

  const renderActiveLocationDetails = () => {
    if (!activeCoordinate) return null

    if (activeCoordinate.source === 'cellular') {
      return (
        <>
          <span>このエリアの中にあります。</span>
          <br />
          <span>
            観測時刻:
            {' '}
            {activeCoordinate.timestamp
              ? getFromNowAsText(activeCoordinate.timestamp)
              : '-'}
          </span>
          <br />
          {activeAccuracyM !== null && (
            <span hidden>
              推定の目安: 約
              {Math.round(activeAccuracyM).toLocaleString('ja-JP')}
              {' '}
              m
              <br />
            </span>
          )}
        </>
      )
    }

    if (activeCoordinate.source === 'wifi') {
      return (
        <>
          <span>Wi-Fi情報から推定した位置です。</span>
          <br />
          <span>
            観測時刻:
            {' '}
            {activeCoordinate.timestamp
              ? getFromNowAsText(activeCoordinate.timestamp)
              : '-'}
          </span>
          <br />
        </>
      )
    }

    return (
      <>
        <span>GPSで取得した位置です。</span>
        <br />
        <span>
          観測時刻:
          {' '}
          {activeCoordinate.timestamp
            ? getFromNowAsText(activeCoordinate.timestamp)
            : '-'}
        </span>
        <br />
      </>
    )
  }

  // 初期表示時に最新ログを選択
  useEffect(() => {
    if (coordinates.length > 0) {
      const latestIndex = coordinates.length - 1
      setSelectedLogIndex(latestIndex)
      const latest = coordinates[latestIndex]
      if (latest) {
        setInfoWindowPosition({ lat: latest.lat, lng: latest.lng })
        setShowInfoWindow(true)
      }
      return
    }

    setSelectedLogIndex(0)
    setShowInfoWindow(false)
    setInfoWindowPosition(null)
  }, [coordinates])

  useEffect(() => {
    if (!isLoaded || hasCountedLoadRef.current) {
      return
    }
    incrementGoogleMapsLoadCount('DetailGpsTrackerMap')
    hasCountedLoadRef.current = true
  }, [isLoaded])

  if (coordinates.length === 0) {
    return renderNoLocation()
  }

  if (loadError) {
    return (
      <div className="detail-modal-content-map">
        {renderLocationSourceControl()}
        <div>Map cannot be loaded right now, sorry.</div>
        {renderDateControl()}
      </div>
    )
  }

  if (!isLoaded) {
    return (
      <div className="detail-modal-content-map">
        {renderLocationSourceControl()}
        <div>地図を読み込み中...</div>
        {renderDateControl()}
      </div>
    )
  }

  return (
    <div className="detail-modal-content-map">
      {renderLocationSourceControl()}
      {activeCoordinate?.source === 'cellular' && (
        <div className="gps-tracker-cellular-location-notice" role="status">
          基地局から推定したおおよその位置です。このエリアの中にあります
        </div>
      )}
      <div className="map-view">
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '400px' }}
          center={mapCenter}
          zoom={14}
          onLoad={() => {
            updateOffsetSize()
          }}
        >
          {/* 移動経路を線で表示 */}
          {polylinePath.length > 1 && (
            <Polyline
              path={polylinePath}
              options={{
                strokeColor: '#1F95F4',
                strokeOpacity: 0.77,
                strokeWeight: 10,
              }}
            />
          )}

          {activeCoordinate?.source === 'cellular'
          && activeCoordinate
          && activeAccuracyM !== null
          && activeAccuracyM > 0 && (
            <Circle
              center={{ lat: activeCoordinate.lat, lng: activeCoordinate.lng }}
              radius={activeAccuracyM}
              options={{
                clickable: false,
                fillColor: '#1F95F4',
                fillOpacity: 0.18,
                strokeColor: '#1F95F4',
                strokeOpacity: 0.7,
                strokeWeight: 2,
              }}
            />
          )}

          {activeCoordinate?.source === 'wifi'
          && activeCoordinate
          && activeAccuracyM !== null
          && activeAccuracyM > 0 && (
            <Circle
              center={{ lat: activeCoordinate.lat, lng: activeCoordinate.lng }}
              radius={activeAccuracyM}
              options={{
                clickable: false,
                fillColor: '#1F95F4',
                fillOpacity: 0.12,
                strokeColor: '#1F95F4',
                strokeOpacity: 0.6,
                strokeWeight: 2,
              }}
            />
          )}

          {/* 各ポイントにマーカーを配置 */}
          {activeCoordinate && (
            <Marker
              position={{ lat: activeCoordinate.lat, lng: activeCoordinate.lng }}
              icon={activeCoordinate.source === 'cellular'
                ? {
                  url: CELLULAR_ESTIMATE_MARKER_ICON,
                  scaledSize: new window.google.maps.Size(40, 40),
                  anchor: new window.google.maps.Point(20, 20),
                }
                : {
                  url: firstMapPinIcon,
                  scaledSize: new window.google.maps.Size(48, 48),
                }}
              onClick={() => activeIndex !== null && handleMarkerClick(activeIndex)}
            >
              {/* InfoWindow */}
              {showInfoWindow && infoWindowPosition && (
                <InfoWindow
                  options={infoWindowOptions}
                  position={infoWindowPosition}
                  onCloseClick={() => { setShowInfoWindow(false) }}
                >
                  <div>
                    {renderActiveLocationDetails()}
                    {activeCoordinate.source === 'gps'
                    && currentStatus
                    && currentStatus.gps_satellites > 0 && (
                      <span
                        className="gps-satellites-count"
                        hidden
                        aria-label={`GPS衛星数 ${currentStatus.gps_satellites}個`}
                      >
                        <FontAwesomeIcon icon={faSatellite} aria-hidden="true" />
                        {currentStatus.gps_satellites}
                        個
                      </span>
                    )}
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={`https://www.google.com/maps/search/?api=1&query=${infoWindowPosition.lat},${infoWindowPosition.lng}`}
                    >
                      <span>Googleマップで見る</span>
                    </a>
                  </div>
                </InfoWindow>
              )}
            </Marker>
          )}
        </GoogleMap>
      </div>

      {coordinates.length > 1
      && activeCoordinate
      && activeIndex !== null && (
        <div className="log-info">
          <div className="slider-container" style={{ paddingLeft: '20px', paddingRight: '20px' }}>
            <input
              type="range"
              aria-label="表示時刻"
              aria-valuetext={sliderLabel}
              min={minTimestamp}
              max={maxTimestamp}
              value={sliderValue}
              onChange={(e) => {
                const targetValue = parseInt(e.target.value, 10)
                const closestIndex = coordinates.reduce((closest, coord, index) => {
                  const coordTime = new Date(coord.timestamp).getTime()
                  const closestTime = new Date(coordinates[closest]!.timestamp).getTime()
                  return Math.abs(coordTime - targetValue) < Math.abs(closestTime - targetValue) ? index : closest
                }, 0)
                handleMarkerClick(closestIndex)
              }}
              style={{ maxWidth: '100%', padding: '0px' }}
              className="time-slider"
            />
            <div className="slider-label" style={{ marginLeft: `calc(${sliderPercentage}%)` }}>
              {sliderLabel}
            </div>
          </div>
        </div>
      )}
      {renderDateControl()}
    </div>
  )
}

export default DetailGpsTrackerMap
